import React, { useState } from 'react';
import { InformationCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Case-level scope disclosure: the investigation results do NOT include the
 * non-interactive Entra sign-in feed.
 *
 * Why this exists. `graph_signin_noninteractive` (token refresh / SSO reuse /
 * PRT replay) is NOT part of `silver_m365_unified_audit_events` — it has its own
 * Delta table, and the investigation runbook reads only the unified table. The
 * split is deliberate and permanent on the pipeline side (~3.4M rows/weekday,
 * the largest table in `silver_unified`), so this is a by-design gap, not a
 * failure — and it applies to EVERY case ever run, not to particular ones.
 *
 * The measured harm, on `IR-20260818-Controller-001` (re-derived against live data
 * 2026-08-19, correcting the first pass): of that case's 11 confirmed attacker IPs,
 * 3 were invisible here for THIS reason — including `43.166.208.95`, 224 sessions
 * over 07-19 → 07-24. That is TEN DAYS before the case's `IocDate` of 2026-07-29 —
 * which is the FIRST KNOWN INDICATOR the case was opened from, NOT a finding about
 * when compromise occurred, so do not describe it as "the compromise date" or imply
 * the analyst dated it late. It is NOT ten days before the case's start: earliest attacker
 * activity is 2026-07-09 (`173.252.160.20`), established from UAL and the Exchange
 * backfill, and this feed holds nothing earlier — do not credit the start date to
 * it (corrected 2026-08-19; the first pass overstated this). `DESKTOP-D61DA9`, the
 * device on that IP, appeared nowhere in the case at all. (Two further missing IPs
 * were credential attacks on a DIFFERENT account and are out of any case scoped to
 * this user — not this gap.)
 * That is the specific error this line prevents: taking the first event shown as
 * the start of the intrusion, or the IP list as the complete IOC set.
 *
 * Same principle the DataSourceErrors banner enforces (a partial list must never
 * read as complete), applied to a by-design gap instead of a query failure. So,
 * like that banner, this is NOT dismissible. It is informational in tone rather
 * than amber — nothing went wrong — and collapsed to one line so it does not
 * compete for attention with the banners above it, which report real problems.
 *
 * This began as the disclosure half of the agreed design (their §8 minimum outcome),
 * shipped ahead of the panel. As of v1.147.0 the panel EXISTS — the IP Addresses tab's
 * "Non-interactive sign-ins" action — so the closing sentence now points at it. The
 * notice stays regardless: the timeline and device list are still built without this
 * feed, and the panel only closes the gap once an analyst runs it.
 * See handoff/2026-08-18_Non-Interactive-Signin-Case-Visibility-Portal-Response.md
 * and Plans/2026-08-19_Non-Interactive-Case-Visibility.md
 */
/**
 * @param {object}  props
 * @param {boolean} props.collected  Did THIS case's run actually read the non-interactive
 *   feed? From `results.NonInteractiveCollected` (client.js), which is the run blob's own
 *   key presence — not the array length, because an empty array cannot distinguish "never
 *   collected" from "collected and clean".
 */
export default function CaseScopeNotice({ collected = false }) {
  const [open, setOpen] = useState(false);

  // 🔴 THE GAP IS CLOSED ON THIS CASE, SO SAYING IT IS OPEN IS SIMPLY FALSE (v1.174.0).
  // This notice rendered unconditionally, on the reasoning that the gap was permanent by
  // design — the investigation runbook read only `silver_m365_unified_audit_events`. That
  // stopped being true in v1.149.0, and on 2026-08-26 it printed "non-interactive sign-ins
  // are not included in these results" on a case whose blob carried 172 rows of them
  // (2,261 authentications, 46 of them the attacker's). A stale disclosure is not a
  // harmless one: it tells an analyst the IP list may be short when it is complete, which
  // is how a real finding gets discounted.
  //
  // Nothing is rendered when the feed IS in the results — there is no gap left to disclose,
  // and a reworded "the feed IS included" note would be chrome on every case forever.
  if (collected) return null;

  return (
    <div className="mb-4 px-3 py-2 bg-gray-900/60 border border-gray-800 rounded-lg">
      <div className="flex items-start gap-2">
        <InformationCircleIcon className="h-4 w-4 text-sky-500/80 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-400">
            <span className="text-gray-300 font-medium">Scope:</span>{' '}
            non-interactive sign-ins (token refresh, SSO reuse, PRT replay) are{' '}
            <span className="font-semibold text-gray-300">not included</span> in these results, so the
            timeline, IP list and device list can be incomplete.
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="ml-1.5 inline-flex items-center gap-0.5 text-sky-400 hover:text-sky-300 align-baseline"
            >
              {open ? 'Hide detail' : 'What this means'}
              <ChevronRightIcon className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} />
            </button>
          </p>

          {open && (
            <div className="mt-2 space-y-2 text-xs text-gray-500 border-t border-gray-800 pt-2">
              <p>
                Microsoft logs a non-interactive sign-in whenever an already-issued token is presented —
                no password, no MFA prompt. Those records live in a separate, much larger feed that this
                investigation does not read, so an IP or device the attacker used <span className="italic">only</span>{' '}
                for token replay leaves no trace on this case.
              </p>
              <p>
                <span className="text-gray-400 font-medium">Do not read the earliest event below as the start
                of the activity</span>, and do not treat the IP list as a complete IOC set. On a real case this
                gap hid 3 of the 11 confirmed attacker IPs — one of them running 224 sessions over five
                days — along with the only device that used them. That activity began 10 days before the
                earliest indicator the case was opened from.
              </p>
              <p>
                <span className="text-gray-400 font-medium">To see what is in the gap:</span> open the{' '}
                <span className="text-gray-400 font-medium">IP Addresses</span> tab and run{' '}
                <span className="text-gray-400 font-medium">Non-interactive sign-ins</span>. It queries that
                feed for this case&apos;s user and window and adds any IPs it finds to the table, with their
                devices and the evidence to judge them — classifiable like any other IP. The timeline still
                will not show these events, which is why those rows are badged{' '}
                <span className="text-gray-400 font-medium">non-interactive only</span>.
              </p>
              <p>
                The <span className="text-gray-400 font-medium">Token Use</span> panel on an alert for the same
                user gives a comparable view around an alert. Note that{' '}
                <span className="text-gray-400 font-medium">Check real-user evidence</span> also reads this
                feed, but only to grade IPs already listed; it cannot add the missing ones.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
